import {Inject, Injectable} from '@angular/core';
import {CommonRzmService} from './common-rzm.service';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {ServerConstants} from '../model/ServerConstants';
import {RestEndPoints} from '../model/RestEndPoints';
import {ChangePasswordCommand} from '../generate/ChangePasswordCommand';
import {CompleteUserSignUpCommand} from '../generate/CompleteUserSignUpCommand';
import {UserVO} from '../generate/UserVO';
import {AuthService} from './auth.service';
import {CommandEnvelope} from '../generate/CommandEnvelope';
import {UpdateUserCommand} from '../generate/UpdateUserCommand';
import {CreateUserCommand} from '../generate/CreateUserCommand';
import {AppConfigService} from './app.config.service';
import {TermsAndPrivacyAcknowledgmentCommand} from "../generate/TermsAndPrivacyAcknowledgmentCommand";
import {GenerateRecoveryCodesCommand} from "../generate/GenerateRecoveryCodesCommand";
import {ApiTokenCreatedResponse, ApiTokenModel} from "../components/user-management/api-tokens/api-token.model";

@Injectable({
  providedIn: 'root'
})
export class UserService extends CommonRzmService {

  constructor(@Inject(HttpClient) http, @Inject('env') environment, @Inject(AuthService) auth, @Inject(AppConfigService) private config: AppConfigService) {
    super(http, environment, auth);
  }

  getTldManagerUserByEmail(email: string): Observable<UserVO> { //this is only being used while adding user in auth policy
    const url = this.getRestEndPoint() + RestEndPoints.GET_TLD_MANAGER_USER_BY_EMAIL + email;
    return this.getResponse<UserVO>(url);
  }

  getCurrentUser() {
    const url = this.getRestEndPoint() + RestEndPoints.GET_CURRENT_USER;
    return this.getResponse(url);
  }



  retrieveCurrentUserAuditLog() {
    const url = this.getRestEndPoint() + RestEndPoints.GET_CURRENT_USER_AUDIT_LOG;
    return this.getResponse<any>(url);
  }

  createOrEditUser(userVO: UserVO, signUpMode: boolean) {
    if (!signUpMode) {
      return this.updateUser(userVO);
    } else {
      return this.completeSignUp(userVO);
    }

  }

  updateUser(userVO: UserVO) {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const updateUserCommand: UpdateUserCommand = {};
    updateUserCommand.user = userVO;
    return this.getPostResponse(url, this.createCommandEnvelope(updateUserCommand, ServerConstants.UPDATE_USER_COMMAND));
  }

  completeSignUp(userVO: UserVO) {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const completeUserSignUpCommand: CompleteUserSignUpCommand = {};
    userVO.termsAndPrivacyAcknowledgmentDate = this.config.getTermsAndPrivacyVersion();
    completeUserSignUpCommand.user = userVO;
    return this.getPostResponseUnAuthenticatedRequest(url, this.createCommandEnvelope(completeUserSignUpCommand, ServerConstants.COMPLETE_SIGN_UP_COMMAND));
  }

  inviteUser(userVO: UserVO) {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const createUserCommand: CreateUserCommand = {};
    createUserCommand.user = userVO;
    return this.getPostResponse(url, this.createCommandEnvelope(createUserCommand, ServerConstants.CREATE_USER_COMMAND));
  }

  changePassword(currentPassword: string, newPassword: string, unauthenticatedRequest: boolean) {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const changePasswordCommand: ChangePasswordCommand = {};
    changePasswordCommand.currentPassword = currentPassword;
    changePasswordCommand.newPassword = newPassword;
    let commandEnvelope = this.createCommandEnvelope(changePasswordCommand, ServerConstants.CHANGE_PASSWORD_COMMAND);

    if(unauthenticatedRequest){
      return this.changeExpiredPassword(url, commandEnvelope);
    }
    return this.getPostResponse(url, commandEnvelope);
  }

  setup2FA(): Observable<{ totpuri:string }> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const initiateMFACommand = {};
    return this.getPostResponse(url, this.createCommandEnvelope(initiateMFACommand, ServerConstants.INITIATE_MFA_COMMAND));
  }

  verify2FA(code: string): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const activateMFACommand = {code};
    return this.getPostResponse(url, this.createCommandEnvelope(activateMFACommand, ServerConstants.ACTIVATE_MFA_COMMAND));
  }

  disable2FA(): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const deactivateMFACommand = {};
    return this.getPostResponse(url, this.createCommandEnvelope(deactivateMFACommand, ServerConstants.DISABLE_MFA_COMMAND));
  }

  changeExpiredPassword(url: string, commandEnvelope: CommandEnvelope) {
    return this.getPostResponseUnAuthenticatedRequest(url, commandEnvelope);
  }

  acknowledgeTerms(): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const termsAndPrivacyAcknowledgmentCommand: TermsAndPrivacyAcknowledgmentCommand = {};
    termsAndPrivacyAcknowledgmentCommand.termsAndPrivacyVersion = this.config.getTermsAndPrivacyVersion();
    const commandEnvelope = this.createCommandEnvelope(termsAndPrivacyAcknowledgmentCommand, ServerConstants.TERMS_AND_PRIVACY_ACKNOWLEDGMENT_COMMAND);
    return this.getPostResponse(url, commandEnvelope);
  }

  generateRecoveryCodes(): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const command: GenerateRecoveryCodesCommand = {};
    const commandEnvelope = this.createCommandEnvelope(command, ServerConstants.GENERATE_MFA_RECOVERY_CODES_COMMAND);
    return this.getPostResponse(url, commandEnvelope);
  }

  getExistingApiTokens(userId: string): Observable<ApiTokenModel[]> {
    const url = this.getRestEndPoint() + '/apiTokensMetaData/' + userId;
    return this.getResponse(url);
  }

  createApiToken(userId: string): Observable<ApiTokenCreatedResponse> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    return this.getPostResponse(url, this.createCommandEnvelope({}, ServerConstants.CREATE_API_TOKENS_COMMAND));
  }

  renameApiToken(sessionId: string, tokenName: string): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const command = {
      sessionId: sessionId,
      tokenName: tokenName
    };
    return this.getPostResponse(url, this.createCommandEnvelope(command, ServerConstants.RENAME_API_TOKEN_COMMAND))
  }

  revokeApiToken(sessionId: string, tokenSuffix: string): Observable<any> {
    const url = this.getRestEndPoint() + RestEndPoints.COMMON_COMMAND;
    const command = {
      sessionId: sessionId,
      tokenSuffix : tokenSuffix,
    };
    return this.getPostResponse(url, this.createCommandEnvelope(command, ServerConstants.REVOKE_API_TOKEN_COMMAND));
  }
}
